import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { HashLocationStrategy, LocationStrategy } from "@angular/common";
import { HomeComponent } from "./core/home/home.component";
import { PFrameRoutes } from "./shared/pframe-routes";
import { LoginComponent } from "./authentication/login/login.component";
import { LoggedInGuard } from "./services/logged-in.guard";
import { RolesGuard } from "./services/roles.guard";
import { ChangePasswordComponent } from "./authentication/change-password/change-password.component";
import { ForgotPasswordComponent } from "./authentication/forgot-password/forgot-password.component";
import { JhaUnsavedChangesModule } from "@jkhy/responsive-ui-angular/jha-unsaved-changes";

const routes: Routes = [
  {
    path: "",
    redirectTo: "/" + PFrameRoutes.home,
    pathMatch: "full",
  },
  { path: PFrameRoutes.login, component: LoginComponent },
  {
    path: PFrameRoutes.receipt,
    loadChildren: () =>
      import("./modules/jha-receipt/receipt-builder.module").then(
        (module) => module.ReceiptBuilderModule
      ),
    canActivate: [LoggedInGuard],
  },
  {
    path: PFrameRoutes.home,
    component: HomeComponent,
    canActivate: [LoggedInGuard],
  },
  {
    path: PFrameRoutes.roles,
    loadChildren: () =>
      import("./features/roles/roles.module").then(
        (module) => module.RolesModule
      ),
    canActivate: [LoggedInGuard],
  },
  {
    path: PFrameRoutes.changePassword,
    component: ChangePasswordComponent,
  },
  {
    path: PFrameRoutes.forgotPassword,
    component: ForgotPasswordComponent,
  },
  {
    path: PFrameRoutes.guestUsers,
    loadChildren: () =>
      import("./features/guest-users/guest-users.module").then(
        (module) => module.GuestUsersModule
      ),
    canActivate: [LoggedInGuard, RolesGuard],
    data: {
      requiredPermission: "view",
      requiredService: "guestUser",
      requiredProduct: "adminPortal",
    },
  },
  {
    path: PFrameRoutes.userManagement,
    loadChildren: () =>
      import("./features/users/users.module").then(
        (module) => module.UsersModule
      ),
    canActivate: [LoggedInGuard, RolesGuard],
    data: {
      requiredPermission: "view",
      requiredService: "userManagement",
      requiredProduct: "adminPortal",
    },
  },
  {
    path: PFrameRoutes.userProfile,
    loadChildren: () =>
      import("./features/user-profile/user-profile.module").then(
        (module) => module.UsersProfileModule
      ),
    canActivate: [LoggedInGuard],
  },
  {
    path: PFrameRoutes.designerStudio,
    loadChildren: () =>
      import("./features/designer-studio/designer-studio.module").then(
        (module) => module.DesignerStudioModule
      ),
    canActivate: [LoggedInGuard, RolesGuard],
    data: {
      requiredPermission: "view",
      requiredService: "powerDocs",
      requiredProduct: "adminPortal",
    },
  },
  {
    path: PFrameRoutes.institution,
    loadChildren: () =>
      import("./features/institution/institution.module").then(
        (module) => module.InstitutionModule
      ),
    canActivate: [LoggedInGuard, RolesGuard],
    data: {
      requiredPermission: "view",
      requiredService: "institutionManagement",
      requiredProduct: "adminPortal",
    },
  },
  {
    path: PFrameRoutes.myAppStore,
    loadChildren: () =>
      import("./features/my-apps/my-apps.module").then(
        (module) => module.MyAppsModule
      ),
    canActivate: [LoggedInGuard, RolesGuard],
    data: {
      requiredPermission: "view",
      requiredService: "myPowerFrameApps",
      requiredProduct: "adminPortal",
    },
  },
  {
    path: PFrameRoutes.releaseNotes,
    loadChildren: () =>
      import("./features/release-notes/release-notes.module").then(
        (module) => module.ReleaseNotesModule
      ),
    canActivate: [LoggedInGuard],
    data: {
      requiredPermission: "view",
      requiredService: "releaseNotes",
      requiredProduct: "adminPortal",
    },
  },
  {
    path: PFrameRoutes.serviceConfiguration,
    loadChildren: () =>
      import("./features/page-loader/page-loader.module").then(
        (module) => module.PageLoaderModule
      ),
    canActivate: [LoggedInGuard],
  },
  {
    path: PFrameRoutes.profileConfiguration,
    loadChildren: () =>
      import("./features/page-loader/page-loader.module").then(
        (module) => module.PageLoaderModule
      ),
    canActivate: [LoggedInGuard],
  },
  {
    path: PFrameRoutes.manageProject,
    loadChildren: () =>
      import("./features/page-loader/page-loader.module").then(
        (module) => module.PageLoaderModule
      ),
    canActivate: [LoggedInGuard, RolesGuard],
    data: {
      requiredPermission: "view",
      requiredService: "manageProjects",
      requiredProduct: "adminPortal",
    },
  },
  {
    path: PFrameRoutes.auditLogs,
    loadChildren: () =>
      import("./features/page-loader/page-loader.module").then(
        (module) => module.PageLoaderModule
      ),
    canActivate: [LoggedInGuard, RolesGuard],
    data: {
      requiredPermission: "view",
      requiredService: "auditLogs",
      requiredProduct: "adminPortal",
    },
  },
  {
    path: "**",
    redirectTo: "/" + PFrameRoutes.home,
    pathMatch: "full",
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false, // <-- debugging purposes only
    }),
    JhaUnsavedChangesModule.forRoot(),
  ],
  exports: [RouterModule],
  providers: [
    Location,
    { provide: LocationStrategy, useClass: HashLocationStrategy },
  ],
})
export class AppRoutingModule {}
